import { useEffect, useState } from 'react'

/* =========================================================================
 * IS THIS A PHONE — asked in JS, because one answer cannot live only in CSS.
 *
 * ⚠️ THE BREAKPOINT IS 640px AND IT IS NOT A NEW NUMBER. `styles/cc-mobile.css`
 * already restyles the Command Center's tables into cards at `max-width: 639px`.
 * A second, different threshold in JS would give the app two definitions of
 * "phone" that disagree on a 639px viewport — the class of bug this codebase
 * keeps paying for (two normalizers deciding folder-vs-design separately broke
 * Canva drill-in at v1.36.1). So the number is declared once here and the
 * comment points at the stylesheet that has to match it.
 *
 * ⚠️ WHY NOT JUST CSS. CSS can hide a row; it cannot stop React rendering one.
 * The Command Center's defect at phone width is not that 982 rows look wrong —
 * it is that 982 rows EXIST, each carrying its own interactive controls. A
 * `display: none` leaves every one of them in the DOM, still focusable, still
 * counted, still costing layout. The page size has to be a number the
 * component reasons with, which means it has to be in JS.
 *
 * ⚠️ IT ANSWERS BEFORE THE FIRST PAINT AND KEEPS ANSWERING. The initial state
 * reads matchMedia synchronously rather than defaulting to `false` and
 * correcting in an effect — defaulting to desktop would render the full page
 * once on a phone, which is the exact cost being avoided. The listener then
 * handles rotation and the harness's own viewport changes.
 * ========================================================================= */
export const PHONE_MAX = 639

const query = `(max-width: ${PHONE_MAX}px)`

const read = () => (typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  ? window.matchMedia(query).matches
  /* No matchMedia (test environment, SSR): answer NOT-a-phone. A wrong answer
   * in that direction renders more than it needs to, which is slow; the other
   * direction would silently page a desktop board to a phone size and look
   * like missing records. */
  : false)

export default function usePhone() {
  const [phone, setPhone] = useState(read)

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined
    const mql = window.matchMedia(query)
    const on = (e) => setPhone(e.matches)
    /* addEventListener is the modern API; addListener is kept for the older
     * WebKit the app is actually opened on. */
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', on)
      return () => mql.removeEventListener('change', on)
    }
    mql.addListener(on)
    return () => mql.removeListener(on)
  }, [])

  return phone
}
